import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Container, Theme, useTheme } from "@mui/material";
import ConfirmDialog from "components/ConfirmDialog";
import DeleteIconButton from "components/IconButtons/DeleteIconButton";
import EditIconButton from "components/IconButtons/EditIconButton";
import { LazyTableRow, TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  DefaultProgressionGateRequest,
  ListResponseOfSkuProgressionGate,
  OrganizationDetails,
  SkuProgressionGate,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import ProgressionGateForm from "components/ProgressionGateForm";

interface Props {
  organization: OrganizationDetails;
}

interface ProgressionGateRow
  extends Omit<SkuProgressionGate, "id">,
    LazyTableRow {
  id: string;
}

const useStyles = (_: Theme) => ({
  table: {
    "& .MuiTableBody-root": {
      wordBreak: "break-word",
    },
  },
});

const OrganizationDefaultProgressionGates = ({ organization }: Props) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { organizationService, skuService } = useGlobalContext();
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();
  const [createOpen, createOpenActions] = useBoolean(false);
  const [deleting, setDeleting] = useState<SkuProgressionGate | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [
    progressionGateToEdit,
    setProgressionGateToEdit,
  ] = useState<SkuProgressionGate | null>(null);

  const { data, isFetching } = useStrictQuery<ListResponseOfSkuProgressionGate>(
    ["defaultProgressionGates", organization.id],
    () =>
      organizationService.getDefaultProgressionGates(
        DefaultProgressionGateRequest.fromJS({
          sortField: "createdOn",
          sortDescending: true,
          organizationId: organization.id,
        })
      )
  );

  const defaultProgressionGatesData = (data?.data ?? []).map((gate) => ({
    ...gate,
    id: gate.id!,
  })) as ProgressionGateRow[];

  const handleDelete = async () => {
    if (!deleting) return;

    try {
      await registerPromise(skuService.deleteSkuProgressionGate(deleting.id!));

      queryClient.invalidateQueries([
        "defaultProgressionGates",
        organization.id,
      ]);
      setDeleting(null);
      setSnackbarMessage({
        message: t({ id: "courseProgressionGateDeleted" }),
        severity: Severity.Success,
      });
    } catch (e) {
      setSnackbarMessage({
        message: translateError(e, t),
        severity: Severity.Error,
      });
    }
  };

  const Buttons = ({ value }: { value: ProgressionGateRow }) => {
    return (
      <Box display="flex" justifyContent="center">
        <EditIconButton
          size="small"
          onClick={() => {
            setProgressionGateToEdit(value);
            createOpenActions.setTrue();
          }}
        />
        <DeleteIconButton size="small" onClick={() => setDeleting(value)} />
      </Box>
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "skuFormatName",
      align: "left",
      type: "text",
      label: t({ id: "skuFormatLabel" }),
    },
    {
      accessor: "progressionGateTypeName",
      align: "left",
      type: "text",
      label: t({ id: "gateTypeLabel" }),
    },
    {
      accessor: "dialogueTitle",
      align: "left",
      type: "text",
      label: t({ id: "dialogueTitleLabel" }),
    },
    {
      accessor: "prompt",
      align: "left",
      type: "text",
      label: t({ id: "promptLabel" }),
    },
    {
      accessor: "label",
      align: "left",
      type: "text",
      label: t({ id: "placeholderLabel" }),
    },
    {
      accessor: "value",
      align: "left",
      type: "text",
      label: t({ id: "courseProgressionRequiredResponseLabel" }),
    },
    {
      accessor: "errorMessage",
      align: "left",
      type: "text",
      label: t({ id: "errorMessageField" }),
    },
    {
      accessor: "value",
      align: "center",
      type: "node",
      View: Buttons,
      label: t({ id: "actions" }),
      noSort: true,
    },
  ];

  return (
    <Container>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={createOpenActions.setTrue}
        >
          <T id="addDefaultProgressionGate" />
        </Button>
      </Box>

      <Box sx={styles.table}>
        <SortableTable
          rows={defaultProgressionGatesData}
          loading={isFetching}
          dense={true}
          cells={cells}
          defaultOrderBy="createdOn"
        />
      </Box>

      {createOpen && (
        <ProgressionGateForm
          organization={organization}
          progressionGate={progressionGateToEdit}
          onClose={() => {
            createOpenActions.setFalse();
            setProgressionGateToEdit(null);
          }}
          onCreate={() => {
            createOpenActions.setFalse();
            setProgressionGateToEdit(null);
            setSnackbarMessage({
              message: t({ id: "courseProgressionGateSaved" }),
              severity: Severity.Success,
            });
          }}
          onUpdate={() => {
            createOpenActions.setFalse();
            setProgressionGateToEdit(null);
            setSnackbarMessage({
              message: t({ id: "courseProgressionGateUpdated" }),
              severity: Severity.Success,
            });
          }}
        />
      )}

      <ConfirmDialog
        dialogTitleKey="deleteDefaultProgressionGateTitle"
        contentKey="deleteDefaultProgressionGateConfirmation"
        open={!!deleting}
        onCancel={() => setDeleting(null)}
        handleConfirm={handleDelete}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default OrganizationDefaultProgressionGates;
